/**
 * The firm record, fetched once while Settings is open and shared by the
 * tabs that read it. A save hands back the fresh record (`replace`) instead
 * of refetching.
 */
import { useCallback, useEffect, useState } from "react";

import { LawFirmsAPI } from "@/api/lawFirms";
import type { LawFirm } from "@/api/types/lawFirm";

export type SettingsFirm = {
  firm: LawFirm | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
  replace: (firm: LawFirm) => void;
};

export const useSettingsFirm = (
  lawFirmId: string | null,
  enabled: boolean
): SettingsFirm => {
  const [firm, setFirm] = useState<LawFirm | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled || !lawFirmId) {
      setFirm(null);
      setLoading(false);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    LawFirmsAPI.getById(lawFirmId)
      .then((next) => {
        if (!cancelled) setFirm(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFirm(null);
        setError(err instanceof Error ? err.message : "Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lawFirmId, enabled, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  const replace = useCallback((next: LawFirm) => setFirm(next), []);

  return { firm, loading, error, retry, replace };
};
